import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
    root: {
        minHeight: "100vh",
        background: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 16px",
    },
    card: {
        width: "100%",
        maxWidth: "720px",
        background: "#ffffff",
        border: "1px solid #dde3ed",
        borderRadius: "20px",
        overflow: "hidden",
        boxShadow: "0 8px 40px rgba(14,73,123,0.10)",
    },
    banner: {
        background: "linear-gradient(135deg, #0e497b 0%, #0a3560 100%)",
        padding: "28px 32px",
        display: "flex",
        alignItems: "center",
        gap: "18px",
    },
    iconBox: {
        width: "50px",
        height: "50px",
        borderRadius: "14px",
        background: "rgba(255,255,255,0.15)",
        border: "1px solid rgba(255,255,255,0.25)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    bannerTitle: {
        margin: 0,
        fontSize: "20px",
        fontWeight: 700,
        color: "#ffffff",
        letterSpacing: "-0.3px",
    },
    bannerSub: {
        margin: "4px 0 0",
        fontSize: "13px",
        color: "rgba(255,255,255,0.65)",
    },
    body: { padding: "32px" },
    alert: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "13px 16px",
        borderRadius: "10px",
        fontSize: "13.5px",
        fontWeight: 500,
        marginBottom: "24px",
    },
    alertSuccess: {
        background: "#f0fdf4",
        border: "1px solid #86efac",
        color: "#15803d",
    },
    alertError: {
        background: "#fef2f2",
        border: "1px solid #fca5a5",
        color: "#dc2626",
    },
    row: {
        display: "grid",
        gridTemplateColumns: "1fr 52px 1fr",
        alignItems: "start",
    },
    panel: {
        background: "#f8fafd",
        border: "1.5px solid #dde3ed",
        borderRadius: "14px",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "14px",
    },
    panelHeader: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        marginBottom: "2px",
    },
    tag: {
        fontSize: "10px",
        fontWeight: 800,
        padding: "3px 8px",
        borderRadius: "5px",
        letterSpacing: "0.08em",
    },
    tagOut: {
        background: "#fef2f2",
        color: "#dc2626",
        border: "1px solid #fca5a5",
    },
    tagIn: {
        background: "#f0fdf4",
        color: "#15803d",
        border: "1px solid #86efac",
    },
    panelLabel: {
        fontSize: "14px",
        fontWeight: 600,
        color: "#1e3a5f",
        margin: 0,
    },
    helperText: {
        fontSize: "11.5px",
        color: "#8fa3bc",
        margin: 0,
    },
    selectWrap: { position: "relative" },
    select: {
        width: "100%",
        padding: "11px 36px 11px 13px",
        borderRadius: "10px",
        border: "1.5px solid #c8d5e3",
        background: "#ffffff",
        color: "#1e3a5f",
        fontSize: "13px",
        appearance: "none",
        cursor: "pointer",
        outline: "none",
        boxSizing: "border-box",
    },
    selectArrow: {
        position: "absolute",
        right: "12px",
        top: "50%",
        transform: "translateY(-50%)",
        color: "#8fa3bc",
        pointerEvents: "none",
        fontSize: "11px",
    },
    skeleton: {
        height: "44px",
        borderRadius: "10px",
        background: "linear-gradient(90deg, #f0f4f8 25%, #e2eaf2 50%, #f0f4f8 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.4s infinite",
    },
    chip: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "11px 13px",
        borderRadius: "10px",
        border: "1px solid #dde3ed",
        background: "#ffffff",
    },
    avatar: {
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        background: "linear-gradient(135deg, #0e497b, #1a6fb5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontWeight: 700,
        fontSize: "14px",
        flexShrink: 0,
    },
    chipName: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#1e3a5f",
        margin: 0,
    },
    chipMeta: {
        fontSize: "11px",
        color: "#8fa3bc",
        margin: "3px 0 0",
    },
    middleCol: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: "30px",
        gap: "4px",
    },
    arrowDot: {
        width: "3px",
        height: "3px",
        borderRadius: "50%",
        background: "#c8d5e3",
    },
    arrowCircle: {
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        background: "#eaf1f8",
        border: "1.5px solid #c8d5e3",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "5px 0",
    },
    summary: {
        marginTop: "24px",
        background: "#ffffff",
        border: "1.5px solid #ffffff",
        borderRadius: "14px",
        padding: "18px 20px",
    },
    summaryHeader: {
        fontSize: "10px",
        fontWeight: 800,
        letterSpacing: "0.1em",
        color: "#5a82a6",
        textTransform: "uppercase",
        margin: "0 0 14px",
    },
    summaryRow: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        fontSize: "13.5px",
        marginBottom: "6px",
    },
    summaryKey: {
        color: "#5a82a6",
        fontWeight: 600,
        minWidth: "60px",
    },
    summaryVal: {
        color: "#1e3a5f",
        fontWeight: 500,
    },
    footer: {
        display: "flex",
        justifyContent: "flex-end",
        gap: "12px",
        marginTop: "28px",
        paddingTop: "24px",
        borderTop: "1px solid #dde3ed",
    },
    resetBtn: {
        padding: "11px 22px",
        borderRadius: "10px",
        border: "1.5px solid #c8d5e3",
        background: "#ffffff",
        color: "#5a82a6",
        fontSize: "13.5px",
        fontWeight: 600,
        cursor: "pointer",
    },
    empListSection: {
        marginTop: "24px",
        background: "#f8fafd",
        border: "1.5px solid #dde3ed",
        borderRadius: "14px",
        padding: "20px",
    },
    empListHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "14px",
    },
    empListTitle: {
        fontSize: "12px",
        fontWeight: 800,
        letterSpacing: "0.08em",
        color: "#5a82a6",
        textTransform: "uppercase",
        margin: 0,
    },
    empListBadge: {
        fontSize: "11px",
        fontWeight: 700,
        padding: "3px 10px",
        borderRadius: "20px",
        background: "#dde3ed",
        color: "#5a82a6",
    },
    empListBadgeActive: {
        background: "#dbeafe",
        color: "#2563eb",
    },
    empCheckRow: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "10px 12px",
        borderRadius: "10px",
        border: "1.5px solid transparent",
        cursor: "pointer",
        transition: "all 0.15s",
        marginBottom: "6px",
        background: "#ffffff",
    },
    empCheckRowSelected: {
        border: "1.5px solid #93c5fd",
        background: "#eff6ff",
    },
    checkbox: {
        width: "17px",
        height: "17px",
        borderRadius: "5px",
        border: "2px solid #c8d5e3",
        background: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "all 0.15s",
    },
    checkboxChecked: {
        border: "2px solid #3b82f6",
        background: "#3b82f6",
    },
    empEmpty: {
        textAlign: "center",
        padding: "24px 0",
        color: "#8fa3bc",
        fontSize: "13px",
    },
    selectAllRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "12px",
        paddingBottom: "10px",
        borderBottom: "1px solid #dde3ed",
    },
    selectAllBtn: {
        fontSize: "12px",
        fontWeight: 600,
        color: "#3b82f6",
        cursor: "pointer",
        background: "none",
        border: "none",
        padding: 0,
    },
};

// ─── Helper: fetch all pages ──────────────────────────────────────────────────
// backUrl is passed as a parameter because this function is outside React
// and cannot call useContext. Without it, relative `next` URLs would break.
async function fetchAllPages(initialUrl, headers, backUrl) {
    let results = [];
    let url = initialUrl;
    while (url) {
        const res = await axios.get(url, { headers });
        const data = res.data;
        if (Array.isArray(data)) {
            results = [...results, ...data];
            break;
        }
        results = [...results, ...(data.results || [])];
        if (data.next) {
            // Handle both absolute and relative next URLs from DRF pagination
            url = data.next.startsWith("http") ? data.next : `${backUrl}${data.next}`;
        } else {
            url = null;
        }
    }
    return results;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function EmployeeChip({ emp }) {
    const name = [emp.salutation, emp.first_name, emp.last_name].filter(Boolean).join(" ");
    return (
        <div style={S.chip}>
            <div style={S.avatar}>{(emp.first_name?.[0] || "?").toUpperCase()}</div>
            <div>
                <p style={S.chipName}>{name || emp.emp_code}</p>
                <p style={S.chipMeta}>
                    {emp.emp_code} &middot; {emp.designation_name} &middot; {emp.department_name}
                </p>
            </div>
        </div>
    );
}

function SelectPanel({ label, tag, tagStyle, helperText, value, onChange, options, loading, disabledId }) {
    const selected = options.find((o) => String(o.id) === String(value));
    return (
        <div style={S.panel}>
            <div>
                <div style={S.panelHeader}>
                    <span style={{ ...S.tag, ...tagStyle }}>{tag}</span>
                    <p style={S.panelLabel}>{label}</p>
                </div>
                <p style={S.helperText}>{helperText}</p>
            </div>
            <div style={S.selectWrap}>
                {loading ? (
                    <div style={S.skeleton} />
                ) : (
                    <>
                        <select
                            style={S.select}
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                        >
                            <option value="">— Select —</option>
                            {options
                                .filter((o) => String(o.id) !== String(disabledId))
                                .map((o) => {
                                    const name = [o.salutation, o.first_name, o.last_name]
                                        .filter(Boolean)
                                        .join(" ");
                                    return (
                                        <option key={o.id} value={String(o.id)}>
                                            {name} · {o.department_name}
                                        </option>
                                    );
                                })}
                        </select>
                        <span style={S.selectArrow}>▾</span>
                    </>
                )}
            </div>
            {selected && <EmployeeChip emp={selected} />}
        </div>
    );
}

function Spinner() {
    return (
        <svg
            width="15" height="15" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
            style={{ animation: "spin 0.75s linear infinite" }}
        >
            <style>{`
                @keyframes spin    { from { transform: rotate(0deg); }    to { transform: rotate(360deg); } }
                @keyframes shimmer { 0%   { background-position: 200% 0; } 100% { background-position: -200% 0; } }
            `}</style>
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
    );
}

function ReportingEmployeeList({ employees, loading, selectedIds, onToggle, onToggleAll }) {
    const allSelected = employees.length > 0 && employees.every((e) => selectedIds.includes(e.id));

    if (loading) {
        return (
            <div style={S.empListSection}>
                <div style={S.empListHeader}>
                    <p style={S.empListTitle}>Employees Reporting to Outgoing HOD</p>
                </div>
                {[1, 2, 3].map((i) => (
                    <div key={i} style={{ ...S.skeleton, height: "56px", marginBottom: "8px" }} />
                ))}
            </div>
        );
    }

    return (
        <div style={S.empListSection}>
            <div style={S.empListHeader}>
                <p style={S.empListTitle}>Employees Reporting to Outgoing HOD</p>
                <span style={{
                    ...S.empListBadge,
                    ...(selectedIds.length > 0 ? S.empListBadgeActive : {}),
                }}>
                    {selectedIds.length}/{employees.length} selected
                </span>
            </div>

            {employees.length === 0 ? (
                <div style={S.empEmpty}>No employees reporting to this HOD.</div>
            ) : (
                <>
                    <div style={S.selectAllRow}>
                        <span style={{ fontSize: "12px", color: "#8fa3bc" }}>
                            Select employees to transfer to the new HOD
                        </span>
                        <button style={S.selectAllBtn} onClick={() => onToggleAll(!allSelected)}>
                            {allSelected ? "Deselect All" : "Select All"}
                        </button>
                    </div>

                    {employees.map((emp) => {
                        const isSelected = selectedIds.includes(emp.id);
                        const name = [emp.salutation, emp.first_name, emp.last_name].filter(Boolean).join(" ");
                        return (
                            <div
                                key={emp.id}
                                style={{ ...S.empCheckRow, ...(isSelected ? S.empCheckRowSelected : {}) }}
                                onClick={() => onToggle(emp.id)}
                            >
                                <div style={{ ...S.checkbox, ...(isSelected ? S.checkboxChecked : {}) }}>
                                    {isSelected && (
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    )}
                                </div>
                                <div style={S.avatar}>{(emp.first_name?.[0] || "?").toUpperCase()}</div>
                                <div style={{ flex: 1 }}>
                                    <p style={S.chipName}>{name || emp.emp_code}</p>
                                    <p style={S.chipMeta}>
                                        {emp.emp_code} &middot; {emp.designation_name} &middot; {emp.department_name}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </>
            )}
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ChangeHod() {
    // ✅ ROOT FIX: useContext MUST be inside the component — your previous code
    // had this called at the module level, making backUrl always undefined,
    // which caused every API call to fetch from "undefined/api/..." and fail.
    const { authData, backUrl } = useContext(AuthContext);

    const [hods, setHods] = useState([]);
    const [allEmployees, setAllEmployees] = useState([]);
    const [reportingEmployees, setReportingEmployees] = useState([]);
    const [outgoingId, setOutgoingId] = useState("");
    const [incomingId, setIncomingId] = useState("");
    const [selectedEmpIds, setSelectedEmpIds] = useState([]);
    const [loadingHods, setLoadingHods] = useState(true);
    const [loadingEmps, setLoadingEmps] = useState(true);
    const [loadingReporting, setLoadingReporting] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");

    const headers = { Authorization: `Bearer ${authData?.access}` };

    const fetchHods = async () => {
        setLoadingHods(true);
        try {
            // ✅ Pass backUrl as 3rd arg so fetchAllPages can resolve relative next URLs
            const results = await fetchAllPages(
                `${backUrl}/api/emp/employees/?is_hod=1`,
                headers,
                backUrl
            );
            setHods(results);
        } catch {
            setError("Failed to load HOD list.");
        } finally {
            setLoadingHods(false);
        }
    };

    const fetchAllEmployees = async () => {
        setLoadingEmps(true);
        try {
            // ✅ Pass backUrl as 3rd arg
            const results = await fetchAllPages(
                `${backUrl}/api/emp/employees/`,
                headers,
                backUrl
            );
            setAllEmployees(results);
        } catch {
            setError("Failed to load employee list.");
        } finally {
            setLoadingEmps(false);
        }
    };

    const fetchReportingEmployees = async (hodId) => {
        setLoadingReporting(true);
        setReportingEmployees([]);
        setSelectedEmpIds([]);
        try {
            // ✅ Pass backUrl as 3rd arg
            const results = await fetchAllPages(
                `${backUrl}/api/emp/employees/?reporting_to=${hodId}`,
                headers,
                backUrl
            );
            setReportingEmployees(results);
        } catch {
            setError("Failed to load reporting employees.");
        } finally {
            setLoadingReporting(false);
        }
    };

    useEffect(() => {
        fetchHods();
        fetchAllEmployees();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOutgoingChange = (v) => {
        setOutgoingId(v);
        setIncomingId("");
        setSuccess(false);
        setError("");
        setReportingEmployees([]);
        setSelectedEmpIds([]);
        if (v) fetchReportingEmployees(v);
    };

    const handleToggleEmp = (id) => {
        setSelectedEmpIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleToggleAll = (selectAll) => {
        setSelectedEmpIds(selectAll ? reportingEmployees.map((e) => e.id) : []);
    };

    const reset = () => {
        setOutgoingId("");
        setIncomingId("");
        setReportingEmployees([]);
        setSelectedEmpIds([]);
        setSuccess(false);
        setError("");
    };

    const replaceHod = async () => {
        setSubmitting(true);
        setSuccess(false);
        setError("");
        try {
            await axios.post(
                `${backUrl}/api/emp/hod-replacement/`,
                {
                    outgoing_hod_id: Number(outgoingId),
                    incoming_hod_id: Number(incomingId),
                    employee_ids: selectedEmpIds,
                },
                { headers }
            );
            setSuccess(true);
            reset();
            fetchHods();
            fetchAllEmployees();
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "Replacement failed. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const outgoingEmp = hods.find((h) => String(h.id) === String(outgoingId));
    const incomingEmp = allEmployees.find((e) => String(e.id) === String(incomingId));
    const getName = (emp) =>
        [emp?.salutation, emp?.first_name, emp?.last_name].filter(Boolean).join(" ") ||
        emp?.emp_code || "";

    const canSubmit = outgoingId && incomingId && outgoingId !== incomingId && !submitting;

    const submitBtnStyle = {
        padding: "11px 28px",
        borderRadius: "10px",
        border: "none",
        background: canSubmit ? "#0e497b" : "#0e497b",
        color: canSubmit ? "#fff" : "#8fa3bc",
        fontSize: "13.5px",
        fontWeight: 700,
        cursor: canSubmit ? "pointer" : "not-allowed",
        boxShadow: canSubmit ? "0 4px 18px #0e497b" : "none",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        transition: "all 0.2s",
    };

    return (
        <div style={S.root}>
            <div style={S.card}>
                {/* Banner */}
                <div style={S.banner}>
                    <div style={S.iconBox}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                    </div>
                    <div>
                        <h2 style={S.bannerTitle}>Replace Head of Department</h2>
                        <p style={S.bannerSub}>Select outgoing HOD, then choose which employees to transfer</p>
                    </div>
                </div>

                <div style={S.body}>
                    {/* Alerts */}
                    {success && (
                        <div style={{ ...S.alert, ...S.alertSuccess }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            HOD replaced successfully!
                        </div>
                    )}
                    {error && (
                        <div style={{ ...S.alert, ...S.alertError }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            {error}
                        </div>
                    )}

                    {/* HOD Selection Row */}
                    <div style={S.row}>
                        <SelectPanel
                            label="Outgoing HOD"
                            tag="OUT"
                            tagStyle={S.tagOut}
                            helperText="Current head being replaced"
                            value={outgoingId}
                            onChange={handleOutgoingChange}
                            options={hods}
                            loading={loadingHods}
                            disabledId={incomingId}
                        />

                        <div style={S.middleCol}>
                            <div style={S.arrowDot} />
                            <div style={S.arrowDot} />
                            <div style={S.arrowCircle}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <polyline points="19 12 12 19 5 12" />
                                </svg>
                            </div>
                            <div style={S.arrowDot} />
                            <div style={S.arrowDot} />
                        </div>

                        <SelectPanel
                            label="Incoming HOD"
                            tag="IN"
                            tagStyle={S.tagIn}
                            helperText="New head taking over"
                            value={incomingId}
                            onChange={(v) => {
                                setIncomingId(v);
                                setSuccess(false);
                                setError("");
                            }}
                            options={allEmployees}
                            loading={loadingEmps}
                            disabledId={outgoingId}
                        />
                    </div>

                    {/* Reporting Employees Checklist */}
                    {outgoingId && (
                        <ReportingEmployeeList
                            employees={reportingEmployees}
                            loading={loadingReporting}
                            selectedIds={selectedEmpIds}
                            onToggle={handleToggleEmp}
                            onToggleAll={handleToggleAll}
                        />
                    )}

                    {/* Transfer Summary */}
                    {outgoingEmp && incomingEmp && (
                        <div style={S.summary}>
                            <p style={S.summaryHeader}>Transfer Preview</p>
                            <div style={S.summaryRow}>
                                <span style={S.summaryKey}>From</span>
                                <span style={S.summaryVal}>{getName(outgoingEmp)} — {outgoingEmp.department_name}</span>
                            </div>
                            <div style={{ ...S.summaryRow, marginLeft: "6px", marginBottom: "4px" }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <polyline points="19 12 12 19 5 12" />
                                </svg>
                            </div>
                            <div style={S.summaryRow}>
                                <span style={S.summaryKey}>To</span>
                                <span style={S.summaryVal}>{getName(incomingEmp)} — {incomingEmp.department_name}</span>
                            </div>
                            {/* {selectedEmpIds.length > 0 && (
                                <div style={{ ...S.summaryRow, marginTop: "8px", paddingTop: "10px", borderTop: "1px solid #c8d5e3" }}>
                                    <span style={S.summaryKey}>Staff</span>
                                    <span style={S.summaryVal}>
                                        {selectedEmpIds.length} employee{selectedEmpIds.length !== 1 ? "s" : ""} will be transferred
                                    </span>
                                </div>
                            )} */}
                        </div>
                    )}

                    {/* Footer */}
                    <div style={S.footer}>
                        <button style={S.resetBtn} onClick={reset} disabled={submitting}>
                            Reset
                        </button>
                        <button style={submitBtnStyle} onClick={replaceHod} disabled={!canSubmit}>
                            {submitting ? <><Spinner />Processing…</> : "Confirm Replacement"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}